<template>
    <transition name="appointment">
        <div v-if="open" id="multiple-refuses-modal" class="multiple-refuses-modal">
            <AppointmentHeader
                :component-id="'multiple-refuses-modal'"
                :back-label="$t('ProductionProgress.MultipleRefuses')"
                :back-event="handleClose"
                :orders="orderListDisplay"
                :status="headerStatus"
                :color="headerColor"
                :is-fixed="false"
                :orderObject="orderObject"
            />

            <div class="content">
                <div class="count-section">
                    <div class="count-stepper">
                        <span class="count-stepper__label">
                            {{ $t("ProductionProgress.AppointmentModal.Card.RefuseCount") }}
                        </span>
                        <div class="count-stepper__controls">
                            <button
                                type="button"
                                class="count-stepper__button"
                                :disabled="entries.length === 0"
                                @click="removeRefuse"
                            >
                                <span class="count-stepper__icon" aria-hidden="true">−</span>
                            </button>
                            <input
                                v-model="countInput"
                                type="text"
                                inputmode="numeric"
                                class="count-stepper__value"
                                @input="onCountInput"
                                @blur="applyCountInput"
                                @keydown.enter.prevent="applyCountInput"
                            />
                            <button
                                type="button"
                                class="count-stepper__button"
                                @click="addRefuse"
                            >
                                <span class="count-stepper__icon" aria-hidden="true">+</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    v-for="(entry, index) in entries"
                    :key="entry.id"
                    class="refuse-entry"
                >
                    <div class="refuse-entry__title">
                        {{ $t("ProductionProgress.MultipleRefusesEntry", { index: index + 1 }) }}
                    </div>

                    <div class="refuse-entry__fields">
                        <div class="quantity-field-card">
                            <div class="quantity-field-card__icon">
                                <RejectionIcon />
                            </div>
                            <div class="quantity-field-card__content">
                                <span class="quantity-field-card__label">{{ $t("REFUSE") }}</span>
                                <input
                                    :value="entry.quantity"
                                    type="text"
                                    inputmode="decimal"
                                    class="quantity-field-card__input"
                                    @focus="handleQuantityFocus(index, $event)"
                                    @keydown="filterQuantityKeydown($event)"
                                    @paste="handleQuantityPaste(index, $event)"
                                    @input="sanitizeQuantityField(index, $event)"
                                />
                            </div>
                            <div class="quantity-field-card__warn">
                                <WarnIcon />
                            </div>
                        </div>

                        <div
                            :class="[
                                'quantity-field-card',
                                {
                                    'quantity-field-card--alert': isReasonAlert(index),
                                    'quantity-field-card--disabled': isReasonDisabled(index),
                                    'quantity-field-card--dropdown-open': openReasonIndex === index
                                }
                            ]"
                        >
                            <div class="quantity-field-card__icon">
                                <ReasonIcon />
                            </div>
                            <div class="quantity-field-card__content">
                                <span class="quantity-field-card__label">
                                    {{ $t("MOTIVO DE REFUGO") }}
                                </span>
                                <div class="reason-select">
                                    <button
                                        type="button"
                                        :class="[
                                            'quantity-field-card__input',
                                            'quantity-field-card__input--select',
                                            'reason-select__trigger',
                                            {
                                                'quantity-field-card__input--placeholder': !entry.reasonId,
                                                'quantity-field-card__input--alert': isReasonAlert(index),
                                                'reason-select__trigger--open': openReasonIndex === index
                                            }
                                        ]"
                                        :disabled="isReasonDisabled(index)"
                                        @click.stop="toggleReasonDropdown(index)"
                                    >
                                        <span class="reason-select__value">
                                            {{ getReasonName(entry.reasonId) || $t("Select") }}
                                        </span>
                                    </button>
                                    <ul
                                        v-if="openReasonIndex === index"
                                        class="reason-select__dropdown"
                                    >
                                        <li
                                            v-for="reason in refuseReasons"
                                            :key="reason.id"
                                            :class="[
                                                'reason-select__option',
                                                {
                                                    'reason-select__option--selected':
                                                        entry.reasonId === reason.id
                                                }
                                            ]"
                                            @click.stop="selectReason(index, reason.id)"
                                        >
                                            {{ reason.name }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="quantity-field-card__warn">
                                <WarnIcon />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="footer-bar">
                <button
                    type="button"
                    class="footer-bar__button"
                    :disabled="!isValid"
                    @click="saveRefuses"
                >
                    {{ $t("ProductionProgress.Save") }}
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import AppointmentHeader from "@/views/pages/operator/components/AppointmentHeader";
import RejectionIcon from "@/assets/images/icons/refuse-icon-1.svg";
import ReasonIcon from "@/assets/images/icons/reason-icon-1.svg";
import WarnIcon from "@/assets/images/icons/warn-icon-1.svg";

let entryIdCounter = 0;

const emptyEntry = () => ({
    id: ++entryIdCounter,
    quantity: "0",
    reasonId: null
});

export default {
    name: "MultipleRefusesModal",
    components: {
        AppointmentHeader,
        RejectionIcon,
        ReasonIcon,
        WarnIcon
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        rejectionList: {
            type: Array,
            default: () => []
        },
        savedEntries: {
            type: Array,
            default: () => []
        },
        orderObject: {
            type: Object,
            default: () => ({})
        },
        orderListDisplay: {
            type: Array,
            default: () => []
        },
        headerStatus: {
            type: String,
            default: ""
        },
        headerColor: {
            type: String,
            default: ""
        },
        showToast: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            entries: [],
            countInput: "0",
            openReasonIndex: null
        };
    },
    computed: {
        refuseReasons() {
            return (this.rejectionList || []).map((item) => ({
                id: item.value ?? item.id,
                name: item.text || item.name
            }));
        },
        isValid() {
            if (this.entries.length === 0) return false;

            return this.entries.every(
                (entry) => this.formatedNumber(entry.quantity) > 0 && !!entry.reasonId
            );
        }
    },
    watch: {
        open(value) {
            if (value) {
                this.resetForm();
                document.addEventListener("click", this.handleOutsideClick);
            } else {
                document.removeEventListener("click", this.handleOutsideClick);
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideClick);
    },
    methods: {
        resetForm() {
            this.entries = this.savedEntries.map((entry) => ({
                id: ++entryIdCounter,
                quantity: entry.quantity,
                reasonId: entry.reasonId
            }));
            this.countInput = String(this.entries.length);
            this.openReasonIndex = null;
        },
        handleClose() {
            this.openReasonIndex = null;
            this.$emit("update:open", false);
            this.$emit("close");
        },
        handleOutsideClick() {
            this.openReasonIndex = null;
        },
        toggleReasonDropdown(index) {
            if (this.isReasonDisabled(index)) return;
            this.openReasonIndex = this.openReasonIndex === index ? null : index;
        },
        selectReason(index, reasonId) {
            this.$set(this.entries, index, {
                ...this.entries[index],
                reasonId
            });
            this.openReasonIndex = null;
        },
        getReasonName(reasonId) {
            if (!reasonId) return "";
            const reason = this.refuseReasons.find((item) => item.id === reasonId);
            return reason?.name || "";
        },
        addRefuse() {
            this.openReasonIndex = null;
            this.entries = [...this.entries, emptyEntry()];
            this.countInput = String(this.entries.length);
        },
        removeRefuse() {
            if (this.entries.length === 0) return;
            this.openReasonIndex = null;
            this.entries = this.entries.slice(0, -1);
            this.countInput = String(this.entries.length);
        },
        onCountInput(event) {
            this.countInput = String(event.target.value).replace(/\D/g, "").slice(0, 2);
        },
        applyCountInput() {
            const n = Math.max(0, parseInt(this.countInput, 10) || 0);
            this.syncEntries(n);
            this.countInput = String(this.entries.length);
            this.openReasonIndex = null;
        },
        syncEntries(count) {
            const n = Math.max(0, Math.floor(Number(count) || 0));

            if (n > this.entries.length) {
                const toAdd = n - this.entries.length;
                this.entries = [
                    ...this.entries,
                    ...Array.from({ length: toAdd }, () => emptyEntry())
                ];
            } else if (n < this.entries.length) {
                this.entries = this.entries.slice(0, n);
            }
        },
        formatedNumber(value) {
            if (typeof value !== "string") return Number(value) || 0;
            const cleaned = value.replace(/\./g, "").replace(",", ".");
            return parseFloat(cleaned) || 0;
        },
        filterQuantityKeydown(event) {
            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Tab",
                "Home",
                "End",
                "Enter"
            ];

            if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;

            if (event.key === "," || event.key === ".") {
                const value = String(event.target.value).replace(/\./g, ",");

                if (value.includes(",")) {
                    event.preventDefault();
                }

                return;
            }

            if (!/^\d$/.test(event.key)) {
                event.preventDefault();
                return;
            }

            const input = event.target;
            const value = String(input.value).replace(/\./g, ",");
            const start = input.selectionStart ?? value.length;
            const end = input.selectionEnd ?? value.length;
            const newValue = `${value.slice(0, start)}${event.key}${value.slice(end)}`;

            if (this.wouldExceedQuantityLimit(newValue)) {
                event.preventDefault();
            }
        },
        wouldExceedQuantityLimit(value) {
            const raw = String(value ?? "")
                .replace(/\./g, ",")
                .replace(/[^\d,]/g, "");
            const commaIndex = raw.indexOf(",");
            const integerDigits = (commaIndex === -1 ? raw : raw.slice(0, commaIndex)).replace(
                /\D/g,
                ""
            );
            const decimalDigits = (
                commaIndex === -1 ? "" : raw.slice(commaIndex + 1).replace(/,/g, "")
            ).replace(/\D/g, "");

            return integerDigits.length > 9 || decimalDigits.length > 9;
        },
        normalizeQuantityInput(value) {
            let raw = String(value ?? "")
                .trim()
                .replace(/\./g, ",");

            if (raw === "") return "0";

            raw = raw.replace(/[^\d,]/g, "");

            const commaIndex = raw.indexOf(",");
            let integerPart = "";
            let decimalPart = "";

            if (commaIndex === -1) {
                integerPart = raw;
            } else {
                integerPart = raw.slice(0, commaIndex);
                decimalPart = raw.slice(commaIndex + 1).replace(/,/g, "");
            }

            integerPart = integerPart.replace(/\D/g, "").slice(0, 9);
            decimalPart = decimalPart.replace(/\D/g, "").slice(0, 9);

            if (integerPart === "") {
                integerPart = "0";
            } else if (integerPart.length > 1) {
                integerPart = integerPart.replace(/^0+/, "") || "0";
            }

            if (commaIndex === -1) {
                return integerPart;
            }

            return decimalPart ? `${integerPart},${decimalPart}` : `${integerPart},`;
        },
        handleQuantityFocus(index, event) {
            const currentValue = String(this.entries[index]?.quantity ?? "");

            if (currentValue !== "0" && currentValue !== 0) return;

            this.$nextTick(() => {
                event.target.select();
            });
        },
        handleQuantityPaste(index, event) {
            event.preventDefault();

            const pasted = (event.clipboardData || window.clipboardData).getData("text");
            const input = event.target;
            const value = String(input.value).replace(/\./g, ",");
            const start = input.selectionStart ?? value.length;
            const end = input.selectionEnd ?? value.length;
            const newValue = `${value.slice(0, start)}${pasted}${value.slice(end)}`;
            const normalized = this.normalizeQuantityInput(newValue);

            this.updateEntryQuantity(index, normalized, input);
        },
        sanitizeQuantityField(index, event) {
            if (!event?.target || !this.entries[index]) return;

            const normalized = this.normalizeQuantityInput(event.target.value);
            this.updateEntryQuantity(index, normalized, event.target);
        },
        updateEntryQuantity(index, normalized, input) {
            if (input) input.value = normalized;

            const entry = {
                ...this.entries[index],
                quantity: normalized
            };

            if (this.formatedNumber(normalized) === 0) {
                entry.reasonId = null;
            }

            this.$set(this.entries, index, entry);
        },
        isReasonDisabled(index) {
            return this.formatedNumber(this.entries[index]?.quantity) <= 0;
        },
        isReasonAlert(index) {
            const entry = this.entries[index];
            if (!entry) return false;

            return this.formatedNumber(entry.quantity) > 0 && !entry.reasonId;
        },
        saveRefuses() {
            if (!this.isValid) {
                this.showToast(
                    this.$t("ProductionProgress.Toasts.RequiredFields.Title"),
                    "FrownIcon",
                    this.$t("ProductionProgress.Toasts.RequiredFields.Content"),
                    "danger"
                );
                return;
            }

            this.$emit(
                "save",
                this.entries.map((entry) => ({
                    quantity: entry.quantity,
                    reasonId: entry.reasonId
                }))
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.multiple-refuses-modal {
    overflow: auto;
    height: 100dvh;
    width: 100vw;
    background: #fcfcfc;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 10000;
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: #fff;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #cfc4be;
        border-radius: 9px;
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px 29px 109px;
        flex-grow: 1;
    }

    .count-section {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    .count-stepper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
        padding: 16px 20px;
        border: 1px solid #cfc4be;
        border-radius: 16px;
        background: #ffffff;

        &__label {
            font-size: 16px;
            font-weight: 600;
            line-height: 26px;
            color: #4c4541;
        }

        &__controls {
            display: flex;
            align-items: center;
            gap: 16px;
        }

        &__value {
            width: 48px;
            min-width: 48px;
            height: 44px;
            text-align: center;
            font-size: 22px;
            font-weight: 600;
            line-height: 30px;
            color: #4c4541;
            border: 1px solid #cfc4be;
            border-radius: 8px;
            background: #ffffff;
            padding: 0 4px;

            &:focus {
                outline: none;
                border-color: #974900;
            }
        }

        &__button {
            width: 44px;
            height: 44px;
            border: 1px solid #974900;
            border-radius: 8px;
            background: #ffede2;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;

            .count-stepper__icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                font-size: 28px;
                font-weight: 400;
                line-height: 1;
                color: #974900;
                user-select: none;
            }

            &:hover:not(:disabled) {
                background: #ffdbc4;
            }

            &:disabled {
                opacity: 0.4;
                cursor: not-allowed;
                border-color: #cfc4be;
                background: #f3efec;

                .count-stepper__icon {
                    color: #998f8a;
                }
            }
        }
    }

    .refuse-entry__fields {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        overflow: visible;
    }

    .refuse-entry {
        background: #ffffff;
        border: 1px solid #cfc4be;
        border-radius: 6px;
        padding: 24px 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow: visible;
        position: relative;

        &__title {
            font-family: "Raleway", sans-serif;
            font-weight: 600;
            font-size: 16px;
            line-height: 26px;
            color: #4c4541;
        }
    }

    .quantity-field-card {
        display: flex;
        align-items: stretch;
        flex: 1 1 calc(50% - 8px);
        min-width: 220px;
        border: 1px solid #ffa726;
        border-radius: 16px;
        background: #fafafa;
        overflow: hidden;
        transition: background-color 0.2s ease, border-color 0.2s ease;

        &--dropdown-open {
            overflow: visible;
            z-index: 5;
        }

        &:hover:not(.quantity-field-card--disabled) {
            background: #ffede2;
            border-color: #974900;

            .quantity-field-card__icon {
                border-color: #974900;

                svg {
                    fill: #974900;
                }
            }

            .quantity-field-card__label {
                color: #974900;
            }

            .quantity-field-card__input:not(:disabled) {
                color: #974900;
                border-color: #974900;
            }

            .quantity-field-card__input--select:not(:disabled):not(
                    .quantity-field-card__input--placeholder
                ):not(.quantity-field-card__input--alert) {
                background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23974900'/%3E%3C/svg%3E");
            }

            .quantity-field-card__warn svg {
                fill: #974900;
            }
        }

        &:active:not(.quantity-field-card--disabled) {
            background: #ffdbc4;
        }

        &__icon {
            min-width: 68px;
            max-width: 68px;
            border-right: 1px solid #ffa726;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                width: 20px;
                height: 20px;
                fill: #ffa726;
            }
        }

        &__content {
            flex: 1;
            padding: 16px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-width: 0;
        }

        &__label {
            font-size: 14px;
            line-height: 20px;
            color: #998f8a;
            margin-bottom: 4px;
        }

        &__input {
            width: 100%;
            height: 40px;
            border: 1px solid #cfc4be;
            border-radius: 6px;
            background: #ffffff;
            font-weight: 600;
            font-size: 16px;
            line-height: 26px;
            color: #4c4541;
            padding: 8px 12px;
            transition: border-color 0.2s ease, background-color 0.2s ease;

            &:focus {
                outline: none;
                border-color: #974900;
            }

            &--select {
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                cursor: pointer;
                padding-right: 32px;
                background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23974900'/%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-position: calc(100% - 12px) center;
                background-size: 8px 6px;
                text-align: left;

                &--placeholder {
                    color: #ffa726;
                    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23ffa726'/%3E%3C/svg%3E");
                }

                &--alert:not(:disabled) {
                    border-color: #ffa726;
                    color: #ffa726;
                    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23ffa726'/%3E%3C/svg%3E");
                }
            }

            &:disabled {
                color: #998f8a;
                background-color: #f3efec;
                border-color: #cfc4be;
                cursor: not-allowed;
            }
        }

        .reason-select {
            position: relative;
            width: 100%;

            &__trigger {
                width: 100%;
                display: flex;
                align-items: center;

                &--open {
                    border-color: #974900;
                    color: #974900;
                }
            }

            &__value {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &__dropdown {
                position: absolute;
                top: calc(100% + 4px);
                left: 0;
                right: 0;
                z-index: 20;
                margin: 0;
                padding: 4px 0;
                list-style: none;
                background: #ffffff;
                border: 1px solid #974900;
                border-radius: 6px;
                box-shadow: 0px 4px 16px rgba(58, 46, 38, 0.12);
                max-height: 200px;
                overflow-y: auto;
            }

            &__option {
                padding: 10px 12px;
                font-weight: 600;
                font-size: 16px;
                line-height: 26px;
                color: #4c4541;
                cursor: pointer;

                &:hover {
                    background: #ffede2;
                    color: #974900;
                }

                &--selected {
                    background: #ffdbc4;
                    color: #974900;
                }
            }
        }

        &__warn {
            display: flex;
            align-items: center;
            padding: 0 16px;

            svg {
                width: 20px;
                height: 20px;
                fill: #ffa726;
            }
        }

        &--alert {
            border-color: #ffa726;
        }

        &--disabled {
            background: #e0e0e0;
            border-color: #7e7570;
            cursor: not-allowed;

            .quantity-field-card__icon {
                border-color: #7e7570;

                svg {
                    fill: #998f8a;
                }
            }

            .quantity-field-card__label {
                color: #7e7570;
            }

            .quantity-field-card__warn svg {
                fill: #998f8a;
            }

            .quantity-field-card__input--select {
                color: #998f8a;
                background-color: #f3efec;
                border-color: #cfc4be;
                background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23998f8a'/%3E%3C/svg%3E");
            }
        }
    }

    .footer-bar {
        display: flex;
        justify-content: flex-end;
        position: fixed;
        bottom: 20px;
        right: 29px;
        z-index: 10;

        .footer-bar__button {
            border: none;
            border-radius: 5px;
            background: #974900;
            color: #ffffff;
            font-weight: 600;
            font-size: 17px;
            line-height: 20px;
            padding: 13px 28px;
            cursor: pointer;
            box-shadow: 0px 4px 24px 0px rgba(58, 46, 38, 0.1);

            &:hover {
                background: #7a3a00;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }
}

.appointment-enter-active,
.appointment-leave-active {
    transition: opacity 0.25s ease-in-out;
}

.appointment-enter,
.appointment-leave-to {
    opacity: 0%;
}

@media (max-width: 767px) {
    .multiple-refuses-modal {
        .quantity-field-card {
            flex: 1 1 100%;

            &__icon {
                min-width: 48px;
                max-width: 48px;
            }

            &__content {
                padding: 12px 16px;
            }

            &__label {
                font-size: 12px;
                line-height: 16px;
            }

            &__input {
                font-size: 14px;
                line-height: 20px;
            }
        }

        .reason-select__option {
            font-size: 14px;
            line-height: 20px;
        }
    }
}

@media (max-width: 575px) {
    .multiple-refuses-modal {
        .content {
            padding: 24px 16px 109px;
        }

        .footer-bar {
            right: 16px;
            left: 16px;

            .footer-bar__button {
                width: 100%;
            }
        }
    }
}
</style>
